
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import Auth from "./pages/Auth";
import EmployeePortal from "./pages/EmployeePortal";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import CookieConsent from "./components/cookie/CookieConsent";
import { supabase } from "@/integrations/supabase/client";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import SetupAccount from './pages/SetupAccount';
import Book from "./pages/Book";

// Service pages
import ManagedIT from "./pages/Services/ManagedIT";
import Cybersecurity from "./pages/Services/Cybersecurity";
import NetworkInfrastructure from "./pages/Services/NetworkInfrastructure";
import CloudSolutions from "./pages/Services/CloudSolutions";
import ProjectConsulting from "./pages/Services/ProjectConsulting";
import SpecialtyServices from "./pages/Services/SpecialtyServices";
import ResidentialServices from "./pages/Services/ResidentialServices";

// Billing pages
import BillingDashboard from "./pages/billing/BillingDashboard";
import EstimatesPage from "./pages/billing/EstimatesPage";
import InvoicesPage from "./pages/billing/InvoicesPage";
import ReceiptsPage from "./pages/billing/ReceiptsPage";
import ContractsPage from "./pages/billing/ContractsPage";

const queryClient = new QueryClient();

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Initial session check:', session ? 'Session found' : 'No session');
      } catch (error) {
        console.error('Error checking auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Router>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/portal" element={<EmployeePortal />} />
              <Route path="/book" element={<Book />} />
              
              {/* Service routes */}
              <Route path="/services/managed-it" element={<ManagedIT />} />
              <Route path="/services/cybersecurity" element={<Cybersecurity />} />
              <Route path="/services/network-infrastructure" element={<NetworkInfrastructure />} />
              <Route path="/services/cloud-solutions" element={<CloudSolutions />} />
              <Route path="/services/project-consulting" element={<ProjectConsulting />} />
              <Route path="/services/specialty-services" element={<SpecialtyServices />} />
              <Route path="/services/residential" element={<ResidentialServices />} />
              
              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Admin />
                  </ProtectedRoute>
                }
              />

              {/* Billing portal routes */}
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <BillingDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/estimates"
                element={
                  <ProtectedRoute>
                    <EstimatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/invoices"
                element={
                  <ProtectedRoute>
                    <InvoicesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/receipts"
                element={
                  <ProtectedRoute>
                    <ReceiptsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/contracts"
                element={
                  <ProtectedRoute>
                    <ContractsPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/setup-account" element={<SetupAccount />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
          </Router>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
